import React from 'react';
import { X, FileText } from 'lucide-react';
import {
  getMembershipTermsEffectiveDate,
  getMembershipTermsHtml,
  getMembershipTermsText,
  getMembershipTermsTitle,
} from '@/lib/membership-terms';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TermsModal({ isOpen, onClose }: TermsModalProps) {
  const title = getMembershipTermsTitle();
  const termsHtml = getMembershipTermsHtml();
  const termsText = getMembershipTermsText();
  const effectiveDate = getMembershipTermsEffectiveDate();

  const termsLines = React.useMemo(() => {
    const normalized = termsText.replace(/\t•\t/g, '• ').replace(/\t/g, '  ');
    return normalized.split('\n');
  }, [termsText]);

  const rendered = React.useMemo(() => {
    function prevNonEmptyLine(idx: number): string {
      for (let i = idx; i >= 0; i--) {
        const t = termsLines[i]?.trim();
        if (t) return t;
      }
      return '';
    }

    function renderDefaultLine(trimmed: string, key: React.Key) {
      const subsectionHeadingMatch = trimmed.match(/^\d+\.\d+\s(.+)$/);
      const isSubsectionHeading =
        Boolean(subsectionHeadingMatch) && subsectionHeadingMatch![1].split(/\s+/).length <= 10;

      const sectionHeadingMatch = trimmed.match(/^\d+\.\s(.+)$/);
      const isSectionHeading = Boolean(sectionHeadingMatch) && sectionHeadingMatch![1].split(/\s+/).length <= 10;

      const isSection = isSectionHeading || trimmed.startsWith('IMPORTANT') || trimmed === 'ACCEPTANCE';

      const isBullet = trimmed.startsWith('• ');
      if (isBullet) {
        const text = trimmed.slice(2).trim();
        return (
          <div key={key} className="flex gap-2 pl-5">
            <span className="text-gray-400">•</span>
            <span className="flex-1 break-words">{text}</span>
          </div>
        );
      }

      const isDocTitle = trimmed.endsWith('Terms & Conditions');
      const isEffectiveDate = trimmed.startsWith('Effective Date:');

      let className = 'break-words';
      if (isSection) className += ' mt-6 font-semibold text-gray-900';
      else if (isSubsectionHeading) className += ' mt-4 font-semibold text-gray-900';
      else if (isDocTitle) className += ' font-semibold text-gray-900';
      else if (isEffectiveDate) className += ' text-gray-500';

      return (
        <div key={key} className={className}>
          {trimmed}
        </div>
      );
    }

    function parseTripletTable(startIdx: number, stopLine: string) {
      const tokens: string[] = [];
      let j = startIdx;
      while (j < termsLines.length) {
        const t = termsLines[j].trim();
        if (t === stopLine) break;
        if (t) tokens.push(t);
        j++;
      }
      if (tokens.length < 6) return null;
      const headers = tokens.slice(0, 3);
      const rowTokens = tokens.slice(3);
      const rows: Array<[string, string, string]> = [];
      for (let k = 0; k + 2 < rowTokens.length; k += 3) {
        rows.push([rowTokens[k], rowTokens[k + 1], rowTokens[k + 2]]);
      }
      return { endIdx: j, headers, rows };
    }

    function parseNTable(startIdx: number, stopLine: string, cols: number) {
      const tokens: string[] = [];
      let j = startIdx;
      while (j < termsLines.length) {
        const t = termsLines[j].trim();
        if (t === stopLine) break;
        if (t) tokens.push(t);
        j++;
      }
      if (tokens.length < cols * 2) return null;
      const headers = tokens.slice(0, cols);
      const rowTokens = tokens.slice(cols);
      const rows: string[][] = [];
      for (let k = 0; k + (cols - 1) < rowTokens.length; k += cols) {
        rows.push(rowTokens.slice(k, k + cols));
      }
      return { endIdx: j, headers, rows };
    }

    const nodes: React.ReactNode[] = [];
    for (let idx = 0; idx < termsLines.length; idx++) {
      const line = termsLines[idx];
      const trimmed = line.trim();

      if (!trimmed) {
        nodes.push(<div key={`sp-${idx}`} className="h-3" />);
        continue;
      }

      // Benefits summary table
      if (trimmed === 'Benefit') {
        const parsed = parseTripletTable(idx, '2.1 Standard Comfort Club');
        if (parsed) {
          nodes.push(
            <div key={`tbl-benefits-${idx}`} className="my-6 overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full text-xs md:text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    {parsed.headers.map((h) => (
                      <th key={h} className="text-left font-semibold px-3 py-2 border-b border-gray-200">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map((r, i) => (
                    <tr key={`${r[0]}-${i}`} className={i % 2 ? 'bg-white' : 'bg-gray-50/40'}>
                      {r.map((cell, cidx) => (
                        <td key={`${cidx}-${cell}`} className="align-top px-3 py-2 border-b border-gray-100">
                          {cell === '—' ? <span className="text-gray-400">—</span> : cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

          idx = parsed.endIdx - 1;
          continue;
        }
      }

      // Pricing tables
      if (trimmed === 'Systems') {
        const prev = prevNonEmptyLine(idx - 1);
        if (prev.startsWith('3.1 Standard Comfort Club Pricing')) {
          const parsed = parseNTable(idx, '3.2 Premier Comfort Club Pricing', 5);
          if (parsed) {
            nodes.push(
              <div key={`tbl-standard-pricing-${idx}`} className="my-6 overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-xs md:text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      {parsed.headers.map((h) => (
                        <th key={h} className="text-left font-semibold px-3 py-2 border-b border-gray-200">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((r, i) => (
                      <tr key={`${r[0]}-${i}`} className={i % 2 ? 'bg-white' : 'bg-gray-50/40'}>
                        {r.map((cell, cidx) => (
                          <td key={`${cidx}-${cell}`} className="align-top px-3 py-2 border-b border-gray-100">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
            idx = parsed.endIdx - 1;
            continue;
          }
        }

        if (prev.startsWith('3.2 Premier Comfort Club Pricing')) {
          const parsed = parseNTable(idx, '3.3 Two Maintenance Visits Per Year Pricing', 5);
          if (parsed) {
            nodes.push(
              <div key={`tbl-premier-pricing-${idx}`} className="my-6 overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-xs md:text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      {parsed.headers.map((h) => (
                        <th key={h} className="text-left font-semibold px-3 py-2 border-b border-gray-200">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((r, i) => (
                      <tr key={`${r[0]}-${i}`} className={i % 2 ? 'bg-white' : 'bg-gray-50/40'}>
                        {r.map((cell, cidx) => (
                          <td key={`${cidx}-${cell}`} className="align-top px-3 py-2 border-b border-gray-100">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
            idx = parsed.endIdx - 1;
            continue;
          }
        }
      }

      nodes.push(renderDefaultLine(trimmed, idx));
    }

    return nodes;
  }, [termsLines]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-500">Effective {effectiveDate}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 text-gray-700">
          {termsHtml ? (
            <iframe
              title={title}
              sandbox=""
              className="w-full h-[70vh] rounded-xl border border-gray-200 bg-white"
              srcDoc={termsHtml}
            />
          ) : (
            <div className="font-sans text-sm leading-relaxed">{rendered}</div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl flex justify-end">
          <button
            onClick={onClose}
            className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 hover:shadow-blue-300"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
}
